import React, { useState } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  TextField,
  Grid,
  makeStyles,
  Typography,
  LinearProgress
} from "@material-ui/core";

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ImageIcon from "@material-ui/icons/Image";

import { useAlert } from "react-alert";

import Api from "../../../Api";
import api from "../../../axios";


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },

  cardHeader: {
    padding: "20px 24px",

    "& .MuiCardHeader-title": {
      fontSize: 17,
      fontWeight: 600,
      color: "#202124",
    },

    "& .MuiCardHeader-subheader": {
      fontSize: 13,
      color: "#6b7280",
      marginTop: 4,
    },
  },

  cardContent: {
    padding: "24px",

    [theme.breakpoints.down("sm")]: {
      padding: "18px",
    },
  },

  previewCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  previewTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 12,
  },

  previewImage: {
    width: "100%",
    height: 220,
    backgroundColor: "#f8f9fa",
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    border: "1px solid #eeeeee",
    borderRadius: 7,
  },

  field: {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      borderRadius: 7,
      minHeight: 46,
      backgroundColor: "#fff",
    },

    "& .MuiInputLabel-root": {
      fontSize: 14,
    },

    "& .MuiInputBase-input": {
      fontSize: 14,
    },
  },

  uploadButton: {
    minWidth: 150,
    height: 42,
    borderRadius: 7,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  progressWrapper: {
    marginTop: 18,
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: 7,
  },

  progressText: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 7,
  },

  emptyPreview: {
    height: 220,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    backgroundColor: "#f8f9fa",
    border: "1px solid #eeeeee",
    borderRadius: 7,
  },

  emptyIcon: {
    fontSize: 42,
    marginBottom: 8,
    color: "#c5c7ca",
  },
}));


// =====================================================
// COMPONENT
// =====================================================

const AuctionDetails = (props) => {
  const classes = useStyles();
  const alert = useAlert();

  const [banner, setBanner] = useState("");
  const [cover, setCover] = useState("");

  const [typeImage, setTypeImage] = useState("");

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);


  // =====================================================
  // IMAGE CHANGE
  // =====================================================

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      return;
    }

    if (typeImage === "1") {
      setBanner(file);
    } else if (typeImage === "0") {
      setCover(file);
    }
  };


  // =====================================================
  // UPLOAD
  // =====================================================

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!typeImage) {
      alert.error("Please select Banner or Cover.");
      return;
    }

    const selectedFile =
      typeImage === "1"
        ? banner
        : cover;

    if (!selectedFile) {
      alert.error("Please select an image to upload.");
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      const formData = new FormData();

      formData.append("files", selectedFile);
      formData.append("set_id", props.IDMAGE);

      const URLIMAGE =
        typeImage === "1"
          ? `${Api.request.URL}/auction/banner`
          : `${Api.request.URL}/auction/cover`;

      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${URLIMAGE}/${props.IDMAGE}`,
        data: formData,

        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentage = Math.round(
              (progressEvent.loaded * 100) /
                progressEvent.total
            );

            setProgress(percentage);
          }
        },
      };

      const response = await api.request(config);

      alert.success(
        response.data.msg || "Image uploaded successfully."
      );

      setProgress(100);

    } catch (error) {
      console.error("Image upload error:", error);

      alert.error(
        error?.response?.data?.msg ||
          "Unable to upload image."
      );

    } finally {
      setLoading(false);
    }
  };


  // =====================================================
  // PREVIEW URL
  // =====================================================

  const bannerPreview = banner
    ? URL.createObjectURL(banner)
    : props.Banner || "/static/blank.jpeg";

  const coverPreview = cover
    ? URL.createObjectURL(cover)
    : props.Cover || "/static/blank.jpeg";


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Box className={classes.root}>

      <Grid container spacing={3}>

        {/* =================================================
            BANNER
        ================================================= */}

        <Grid item xs={12} md={6}>

          <Card className={classes.previewCard}>

            <CardContent className={classes.cardContent}>

              <Typography className={classes.previewTitle}>
                Auction Banner
              </Typography>

              <div
                className={classes.previewImage}
                style={{
                  backgroundImage: `url("${bannerPreview}")`,
                }}
              />

            </CardContent>

          </Card>

        </Grid>


        {/* =================================================
            COVER
        ================================================= */}

        <Grid item xs={12} md={6}>

          <Card className={classes.previewCard}>

            <CardContent className={classes.cardContent}>

              <Typography className={classes.previewTitle}>
                Auction Cover
              </Typography>

              <div
                className={classes.previewImage}
                style={{
                  backgroundImage: `url("${coverPreview}")`,
                }}
              />

            </CardContent>

          </Card>

        </Grid>


        {/* =================================================
            UPLOAD FORM
        ================================================= */}

        <Grid item xs={12}>

          <Card className={classes.card}>

            <CardHeader
              className={classes.cardHeader}
              avatar={<ImageIcon />}
              title="Update Auction Image"
              subheader="Select the image type and upload a new image."
            />

            <Divider />

            <form onSubmit={handleFormSubmit}>

              <CardContent className={classes.cardContent}>

                <Grid container spacing={3}>

                  {/* IMAGE TYPE */}

                  <Grid item xs={12} md={5}>

                    <TextField
                      className={classes.field}
                      fullWidth
                      required
                      select
                      variant="outlined"
                      label="Image Type"
                      value={typeImage}
                      onChange={(e) =>
                        setTypeImage(e.target.value)
                      }
                      disabled={loading}
                    >

                      <MenuItem value="1">
                        Banner
                      </MenuItem>

                      <MenuItem value="0">
                        Cover
                      </MenuItem>

                    </TextField>

                  </Grid>


                  {/* FILE */}

                  <Grid item xs={12} md={7}>

                    <TextField
                      className={classes.field}
                      fullWidth
                      required
                      variant="outlined"
                      type="file"
                      label="Select Image"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        accept:
                          "image/jpeg,image/jpg,image/png,image/webp",
                      }}
                      onChange={handleChange}
                      disabled={loading}
                    />

                  </Grid>

                </Grid>


                {/* =================================================
                    PROGRESS
                ================================================= */}

                {loading && (

                  <Box className={classes.progressWrapper}>

                    <Typography
                      className={classes.progressText}
                    >
                      Uploading image... {progress}%
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={progress}
                    />

                  </Box>

                )}

              </CardContent>


              <Divider />


              {/* =================================================
                  ACTION
              ================================================= */}

              <Box
                display="flex"
                justifyContent="flex-end"
                p={2}
              >

                <Button
                  className={classes.uploadButton}
                  color="primary"
                  variant="contained"
                  type="submit"
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                >
                  {loading
                    ? "Uploading..."
                    : "Upload Image"}
                </Button>

              </Box>

            </form>

          </Card>

        </Grid>

      </Grid>

    </Box>
  );
};


// =====================================================
// PROP TYPES
// =====================================================

AuctionDetails.propTypes = {
  className: PropTypes.string,
  IDMAGE: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  Banner: PropTypes.string,
  Cover: PropTypes.string,
};


export default AuctionDetails;